import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import InputAdornment from '@mui/material/InputAdornment'
import { FormControl } from '@mui/material'

import SearchIcon from '@mui/icons-material/Search'

import { getUserFilterOptionsForDashboard } from '../../util/userFormatting'
import { statusTypeMap, applicantTypeOptions, completedFoiStatuses, inProgressFoiStatuses } from '../../util/foiConsts'
import { config } from '../../config'
import { FlexColumn, LightBGContainer, SpaceBetweenRowContainer } from '../styles/Containers'
import { TextField, SelectMenuItem } from '../styles/TextField'
import { BoldBodyText } from '../styles/Text'
import FOITable from './FOITable'
import Tooltip from '../common/Tooltip'

const FieldWrapper = styled(FormControl)`
  width: 225px;
`

const FlexContainer = styled.div`
  display: flex;
  gap: ${props => (props.gap !== undefined ? props.gap : '15px')};
  align-items: center;
`

function FilteredFOIs({ foiList, users = [], isCompleted = false }) {
  const getStatusOptions = () => {
    let statuses = isCompleted ? completedFoiStatuses : inProgressFoiStatuses

    if (!config.foi.APPLICATION_FEE) {
      statuses = statuses.filter(s => s !== 'AWAITING_APPLICATION_FEE')
    }

    return statuses.map(key => {
      return {
        value: key,
        ...statusTypeMap[key]
      }
    })
  }

  const statusTypeOptions = getStatusOptions()

  const filterByOptions = {
    applicantType: { value: 'applicantType', label: 'Applicant Type', options: applicantTypeOptions },
    status: { value: 'status', label: 'Status', options: statusTypeOptions }
  }
  if (!isCompleted)
    filterByOptions['assignedTo'] = {
      value: 'assignedTo',
      label: 'Assigned To',
      options: getUserFilterOptionsForDashboard(users)
    }

  const [filteredFois, setFilteredFois] = useState(foiList)
  const [searchedFilteredFois, setSearchedFilteredFois] = useState()
  const [searchValue, setSearchValue] = useState('')
  const [filterBy, setFilterBy] = useState('none')
  const [filterValue, setFilterValue] = useState('')

  function searchFois() {
    if (searchValue.trim() === '') {
      setSearchedFilteredFois(filteredFois)
    } else {
      const searchVal = searchValue.toLowerCase()
      const searched = filteredFois.filter(
        p =>
          p.requestorName.toLowerCase().search(searchVal) >= 0 ||
          p.secondaryId.toLowerCase().search(searchVal) >= 0 ||
          p.keywords.some(k => k.label.toLowerCase().includes(searchVal))
      )
      setSearchedFilteredFois(searched)
    }
  }

  useEffect(() => {
    if (filteredFois) searchFois()
  }, [filteredFois])

  useEffect(() => {
    if (['applicantType', 'status'].includes(filterBy)) {
      const filtered = foiList.filter(f => f[filterBy] === filterValue)
      setFilteredFois(filtered)
    } else if (filterBy === 'assignedTo') {
      const filtered = filterValue === 'nobody' ? foiList.filter(f => !f.assignedTo) : foiList.filter(f => f.assignedTo?.id === filterValue)
      setFilteredFois(filtered)
    } else {
      setFilteredFois(foiList)
    }
  }, [filterValue])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchFois()
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [searchValue])

  function onFilterByChange(e) {
    setFilterBy(e.target.value)
    if (e.target.value !== 'none') setFilterValue(filterByOptions[e.target.value].options[0].value)
    else setFilterValue('')
  }

  const onFilterValueChange = e => setFilterValue(e.target.value)
  const handleSearchChange = e => setSearchValue(e.target.value)

  return (
    <FlexColumn gap="15px" width="100%">
      <LightBGContainer padding="25px 30px">
        <SpaceBetweenRowContainer>
          <FlexContainer>
            <BoldBodyText id="filter-type-label">FILTER BY</BoldBodyText>
            <FieldWrapper>
              <TextField select aria-labelledby="filter-type-label" value={filterBy} onChange={onFilterByChange}>
                <SelectMenuItem value="none">None</SelectMenuItem>
                {Object.values(filterByOptions).map(({ value, label }) => (
                  <SelectMenuItem key={value} value={value}>
                    {label}
                  </SelectMenuItem>
                ))}
              </TextField>
            </FieldWrapper>
            {filterBy !== 'none' && (
              <FieldWrapper>
                <TextField aria-label="Filter by value" select value={filterValue} onChange={onFilterValueChange}>
                  {filterByOptions[filterBy].options.map(({ value, label }) => (
                    <SelectMenuItem key={value} value={value}>
                      {label}
                    </SelectMenuItem>
                  ))}
                </TextField>
              </FieldWrapper>
            )}
          </FlexContainer>
          <FlexContainer gap="10px">
            <TextField
              aria-label="Search"
              placeholder="Search..."
              onChange={handleSearchChange}
              value={searchValue}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Tooltip info="FOIs are searched on the FOI ID, Keywords, and Requester Name fields." />
          </FlexContainer>
        </SpaceBetweenRowContainer>
      </LightBGContainer>
      <LightBGContainer padding="30px 50px 40px">
        {searchedFilteredFois && <FOITable foiList={searchedFilteredFois} isCompleted={isCompleted} />}
      </LightBGContainer>
    </FlexColumn>
  )
}

FilteredFOIs.propTypes = {
  foiList: PropTypes.array.isRequired,
  users: PropTypes.array,
  isCompleted: PropTypes.bool
}

export default FilteredFOIs
